import { type InfiniteData, useInfiniteQuery, type UseInfiniteQueryResult } from "@tanstack/react-query";

import type {
  AppTicketDtoPaginationResultDto,
  CreatableTicketCategoryNodeDto,
  SelfDto,
  TicketRequest,
} from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import type { IconComponent } from "@/components/Icon/Icon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import type { TranslationFunction, TranslationKey } from "@/translations";

export function getTicketActions(
  t: TranslationFunction,
  sessionUser: SelfDto,
  onDelete: () => void,
  copyQuickReplyLink: () => void,
): ContextMenuItem[] {
  if (!sessionUser.isSuperAdmin) {
    return [];
  }

  return [
    {
      labels: {
        default: t("page.tickets.table.context-menu.create-quick-reply-link"),
      },
      value: "create-quick-reply-link",
      icon: <Copy01Icon />,
      callbackFn: copyQuickReplyLink,
    },
    {
      labels: {
        default: t("page.tickets.table.context-menu.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      callbackFn: onDelete,
    },
  ];
}

// Depth from current node to deepest (leaf) child node (inclusive)
export const getMaxDepth = (node: CreatableTicketCategoryNodeDto): number => {
  if (!node.children || node.children.length === 0) {
    return 1;
  } else {
    return 1 + Math.max(...node.children.map((child) => getMaxDepth(child)));
  }
};

export const getPathProgress = (currNode: CreatableTicketCategoryNodeDto, distanceTravelled: number): number => {
  // Remaining depth from current node to deepest leaf node (excluding current node)
  const remainingMaxDepth = getMaxDepth(currNode) - 1;
  // Depth from root to deepest leaf node
  const totalDepth = distanceTravelled + remainingMaxDepth;

  // Return in percentage
  return Math.min(distanceTravelled / totalDepth, 1) * 100;
};

export const getVisibilityOptionIcon = (visibility: TicketRequest["visibility"]): IconComponent => {
  switch (visibility) {
    case "private":
      return <User01Icon size={16} />;
    case "sharedWithBuilding":
      return <Building01Icon size={16} />;
    case "sharedWithCommunity":
    default:
      return <Building04Icon size={16} />;
  }
};

export const getVisibilityOptionLabelKey = (visibility: TicketRequest["visibility"]): TranslationKey => {
  switch (visibility) {
    case "private":
      return "page.resident-tickets.create-modal.form.visibility.option.private";
    case "sharedWithBuilding":
      return "page.resident-tickets.create-modal.form.visibility.option.building";
    case "sharedWithCommunity":
    default:
      return "page.resident-tickets.create-modal.form.visibility.option.project";
  }
};

export const usePotentialDuplicateTickets = (
  { isEnabled } = {
    isEnabled: true,
  },
): UseInfiniteQueryResult<InfiniteData<AppTicketDtoPaginationResultDto, unknown>> => {
  const ticketQueries = useTicketQueries();

  return useInfiniteQuery({
    ...ticketQueries.listForResident({
      StatusType: "newAndInProgress",
      Limit: 99, // Fetch all open tickets
      SortBy: "postedAt",
      RepairRequestFilterType: "onlyCollective",
    }),
    retry: 1,
    enabled: isEnabled,
  });
};

const EXCLUDED_FILTER_KEYS = new Set(["sortBy", "tab", "residentFocusMode", "search", "assigneeMode"]);

export function getActiveTicketFilterCount(filter: Record<string, unknown>): number {
  return Object.entries(filter).filter(
    ([key, value]) =>
      !EXCLUDED_FILTER_KEYS.has(key) &&
      ((Array.isArray(value) && value.length > 0) ||
        (typeof value === "string" && value.trim() !== "") ||
        (typeof value === "boolean" && value)),
  ).length;
}
